// API client para apps/quotes/ (Django backend).
// Endpoints admin (autenticados con JWT):
//   GET    /api/v1/admin/quotes/
//   POST   /api/v1/admin/quotes/
//   GET    /api/v1/admin/quotes/{id}
//   PATCH  /api/v1/admin/quotes/{id}
//   PUT    /api/v1/admin/quotes/{id}/line-items
//   DELETE /api/v1/admin/quotes/{id}
//   POST   /api/v1/admin/quotes/{id}/send
//   GET    /api/v1/admin/quotes/{id}/pdf  → PDF binario
//   GET    /api/v1/admin/quotes/templates/
//   POST   /api/v1/admin/quotes/templates/

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  Quote,
  QuoteCreateIn,
  QuoteListFilters,
  QuoteListResponse,
  QuoteTemplate,
  QuoteTemplateCreateIn,
  QuoteUpdateIn,
  SendQuoteResponse,
} from './types';

const DJANGO_PROXY = '/django-api';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

export class QuotesApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'QuotesApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new QuotesApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function requestBlob(path: string): Promise<Blob> {
  const headers = await authHeaders();
  const res = await fetch(`${DJANGO_PROXY}${path}`, { headers });
  if (!res.ok) {
    throw new QuotesApiError(`HTTP ${res.status}`, res.status);
  }
  return res.blob();
}

export const quotesApi = {
  list: (filters: QuoteListFilters = {}): Promise<QuoteListResponse> => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.lead_id) params.set('lead_id', filters.lead_id);
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));
    if (filters.offset !== undefined) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return request<QuoteListResponse>(`/api/v1/admin/quotes/${qs ? `?${qs}` : ''}`);
  },

  get: (id: string): Promise<Quote> =>
    request<Quote>(`/api/v1/admin/quotes/${id}`),

  create: (data: QuoteCreateIn): Promise<Quote> =>
    request<Quote>('/api/v1/admin/quotes/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: QuoteUpdateIn): Promise<Quote> =>
    request<Quote>(`/api/v1/admin/quotes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string): Promise<void> =>
    request<void>(`/api/v1/admin/quotes/${id}`, { method: 'DELETE' }),

  send: (id: string): Promise<SendQuoteResponse> =>
    request<SendQuoteResponse>(`/api/v1/admin/quotes/${id}/send`, {
      method: 'POST',
    }),

  pdfUrl: (id: string): string => `${DJANGO_PROXY}/api/v1/admin/quotes/${id}/pdf`,
  pdfBlob: (id: string): Promise<Blob> =>
    requestBlob(`/api/v1/admin/quotes/${id}/pdf`),

  // Templates
  listTemplates: (): Promise<QuoteTemplate[]> =>
    request<QuoteTemplate[]>('/api/v1/admin/quotes/templates/'),

  createTemplate: (data: QuoteTemplateCreateIn): Promise<QuoteTemplate> =>
    request<QuoteTemplate>('/api/v1/admin/quotes/templates/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteTemplate: (id: string): Promise<void> =>
    request<void>(`/api/v1/admin/quotes/templates/${id}`, { method: 'DELETE' }),
};
